/**
 * T3 (SPRINT-UI-REFOUNDATION-1) — renomear conversa: a ação do `chatStore`,
 * extraída para este módulo porque o store já beira o teto de 500 linhas da
 * catraca de modularidade (`lint:strict`). UMA função para o cabeçalho e a
 * sidebar: otimista, rollback com aviso se o motor falhar, vazio ou > 120
 * caracteres não salva nada.
 */
import toast from 'react-hot-toast'
import i18next from 'i18next'
import { ConversationTitleSchema, renameConversationRequest } from '../lib/api/conversations'
import type { Conversation } from './chatStore'

export type RenameConversationResult =
  | { ok: true }
  | { ok: false; reason: 'INVALID_TITLE' | 'NETWORK' }

type ConversationsSlice = { conversations: Conversation[] }
type SetConversations = (
  updater: (state: ConversationsSlice) => Partial<ConversationsSlice>,
) => void
type GetConversations = () => ConversationsSlice

function withTitle(conversations: Conversation[], id: string, title: string): Conversation[] {
  return conversations.map((c) => (c.id === id ? { ...c, title } : c))
}

interface RenameQueue {
  confirmedTitle: string | undefined
  revision: number
  tail: Promise<void>
}

export function createRenameConversation(set: SetConversations, get: GetConversations) {
  const queues = new Map<string, RenameQueue>()
  return async (id: string, rawTitle: string): Promise<RenameConversationResult> => {
    const parsed = ConversationTitleSchema.safeParse(rawTitle)
    if (!parsed.success) return { ok: false, reason: 'INVALID_TITLE' }
    const queue = queues.get(id) ?? {
      confirmedTitle: get().conversations.find((c) => c.id === id)?.title,
      revision: 0,
      tail: Promise.resolve(),
    }
    queues.set(id, queue)
    const revision = ++queue.revision
    set((state) => ({ conversations: withTitle(state.conversations, id, parsed.data) }))
    // Acknowledgements for this conversation must reach the engine in intent order.
    const result = queue.tail.then(async (): Promise<RenameConversationResult> => {
      try {
        await renameConversationRequest(id, parsed.data)
        queue.confirmedTitle = parsed.data
        return { ok: true }
      } catch {
        // An older failure cannot erase a newer optimistic title. Roll back only
        // to an acknowledged title, never to a queued title that also failed.
        const confirmedTitle = queue.confirmedTitle
        if (queue.revision === revision && confirmedTitle !== undefined) {
          set((state) => ({ conversations: withTitle(state.conversations, id, confirmedTitle) }))
        }
        toast.error(i18next.t('chat.renameFailed'))
        return { ok: false, reason: 'NETWORK' }
      }
    })
    queue.tail = result.then(() => undefined)
    try {
      return await result
    } finally {
      if (queue.revision === revision) queues.delete(id)
    }
  }
}
